/**
 * Leviathan Web - Price Books Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface PriceBook {
  id: string;
  name: string;
  description: string | null;
  pricingMethod: string;
  isDefault: boolean;
  isActive: boolean;
  effectiveDate: string | null;
  expirationDate: string | null;
  _count: {
    prices: number;
    customers: number;
  };
}

interface Stats {
  total: number;
  active: number;
  inactive: number;
  withCustomers: number;
  defaultBook: { id: string; name: string } | null;
}

const PRICING_METHOD_LABELS: Record<string, string> = {
  FIXED: 'Fixed Price',
  RACK_PLUS: 'Rack + Amount',
  MARGIN_PERCENT: 'Margin %',
  COST_PLUS: 'Cost + Amount',
};

interface StatCardProps {
  label: string;
  value: number | string;
  status?: 'operational' | 'warning' | 'critical';
}

function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-3xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

const PriceBooksPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [priceBooks, setPriceBooks] = useState<PriceBook[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchPriceBooks();
  }, [filter, search]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricebooks/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchPriceBooks = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('isActive', filter === 'active' ? 'true' : 'false');
      if (search) params.append('search', search);

      const response = await fetch(`${API_URL}/api/pricebooks?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPriceBooks(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch price books:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6 relative">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Price Books
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Customer Pricing Tiers & Structures
              </p>
            </div>
            <Link
              to="/pricing/pricebooks/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Price Book</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Active" value={stats.active} status="operational" />
          <StatCard label="With Customers" value={stats.withCustomers} />
          <StatCard
            label="Default"
            value={stats.defaultBook?.name || 'None'}
          />
        </div>
      )}

      {/* Filters */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, description..."
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all placeholder:text-text-tertiary"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <div className="flex space-x-2">
              {(['all', 'active', 'inactive'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`flex-1 px-3 py-2 font-mono text-sm uppercase tracking-wider transition-all ${
                    filter === f
                      ? 'bg-amber text-background'
                      : 'bg-background text-text-secondary hover:text-amber border border-panel-border'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearch('');
                setFilter('all');
              }}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Price Books Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-text-tertiary font-mono animate-pulse">
          &gt; Loading price books...
        </div>
      ) : priceBooks.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-text-tertiary font-mono text-lg mb-2">
            &gt; No price books found
          </div>
          <p className="text-text-tertiary font-mono text-sm mb-4">
            {search || filter !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first price book to get started'}
          </p>
          <Link
            to="/pricing/pricebooks/new"
            className="text-amber hover:text-amber/80 font-mono"
          >
            Create your first price book →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {priceBooks.map((priceBook) => (
            <div
              key={priceBook.id}
              onClick={() => navigate(`/pricing/pricebooks/${priceBook.id}`)}
              className="relative cursor-pointer group"
            >
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

              <div className="bg-panel-dark border border-panel-border p-6 hover:border-amber/50 transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-display text-text-primary group-hover:text-amber transition-colors">
                      {priceBook.name}
                    </h3>
                    {priceBook.description && (
                      <p className="text-text-tertiary text-sm font-mono mt-1 line-clamp-2">
                        {priceBook.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    {priceBook.isDefault && (
                      <span className="px-2 py-0.5 text-xs font-mono bg-amber/20 text-amber border border-amber">
                        DEFAULT
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 text-xs font-mono border ${
                        priceBook.isActive
                          ? 'bg-success-green/20 text-success-green border-success-green'
                          : 'bg-gray-500/20 text-gray-400 border-gray-500'
                      }`}
                    >
                      {priceBook.isActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-tertiary font-mono">Method</span>
                    <span className="text-text-primary font-mono">
                      {PRICING_METHOD_LABELS[priceBook.pricingMethod] || priceBook.pricingMethod}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-tertiary font-mono">Products</span>
                    <span className="text-amber font-mono">{priceBook._count.prices}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-tertiary font-mono">Customers</span>
                    <span className="text-amber font-mono">{priceBook._count.customers}</span>
                  </div>
                  {priceBook.effectiveDate && (
                    <div className="flex justify-between">
                      <span className="text-text-tertiary font-mono">Effective</span>
                      <span className="text-text-secondary font-mono">
                        {formatDate(priceBook.effectiveDate)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div
                  className="mt-4 pt-4 border-t border-panel-border flex justify-end space-x-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => navigate(`/pricing/pricebooks/${priceBook.id}/edit`)}
                    className="text-text-secondary hover:text-amber font-mono text-sm"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => navigate(`/pricing/pricebooks/${priceBook.id}`)}
                    className="text-steel hover:text-amber font-mono text-sm"
                    title="View"
                  >
                    👁️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PriceBooksPage;
